import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function VerifyOtp() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resends, setResends] = useState(0);

  if (!user) { navigate("/register"); return null; }
  if (user.email_verified) {
    const p = localStorage.getItem("ss_pending_publish");
    navigate(p ? "/complete-profile" : "/");
    return null;
  }

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setExpired(false);
    try {
      const { data } = await api.post("/auth/verify-otp", { otp });
      localStorage.setItem("ss_token", data.token);
      setUser(data.user);
      toast.success("Welcome to SellStuff!");
      const pending = localStorage.getItem("ss_pending_publish");
      navigate(pending ? "/complete-profile" : "/");
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail);
      if (detail.toLowerCase().includes("expired")) setExpired(true);
      setError(detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resends >= 3) { toast.error("You've reached the maximum number of resends."); return; }
    try {
      await api.post("/auth/resend-otp");
      setResends((n) => n + 1);
      setOtp(""); setError(""); setExpired(false);
      toast.success("A new code has been sent to your email.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not resend code"); }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="verify-otp-screen">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white"><MailCheck className="h-6 w-6" /></div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Check your email</h1>
          <p className="mt-1 text-sm text-slate-500">We sent a 6-digit code to <b className="text-slate-700">{user.email}</b>. Enter it below to verify your account.</p>
        </div>
        <form onSubmit={verify} className="space-y-4">
          <Input
            data-testid="otp-input"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
            placeholder="••••••"
            className="rounded-lg text-center text-2xl tracking-[0.5em]"
          />
          {error && <p className="text-sm font-medium text-red-600" data-testid="otp-error">{error}</p>}
          {expired ? (
            <Button type="button" onClick={resend} data-testid="otp-resend-expired" className="w-full rounded-full bg-primary font-semibold">Resend code</Button>
          ) : (
            <Button type="submit" disabled={loading || otp.length !== 6} data-testid="otp-verify" className="w-full rounded-full bg-primary font-semibold hover:bg-primary/90">{loading ? "Verifying…" : "Verify"}</Button>
          )}
        </form>
        <div className="mt-6 flex items-center justify-between text-sm">
          <button onClick={resend} data-testid="otp-resend" className="font-semibold text-primary hover:underline" disabled={resends >= 3}>
            {resends >= 3 ? "Resend limit reached" : "Resend code"}
          </button>
          <button onClick={() => navigate("/register")} data-testid="otp-go-back" className="text-slate-500 hover:underline">Wrong email? Go back</button>
        </div>
      </div>
    </div>
  );
}
